// src/layouts/SidebarLayout/components/Sidebar.tsx
import React from 'react';
import {
  Drawer,
  Box,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ChevronLeft,
  ChevronRight,
  Warehouse,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useSidebar } from '../../../contexts/SidebarContext';
import SidebarMenu from './SidebarMenu';
import { SidebarLink, LayoutConfig } from '../types';

interface SidebarProps {
  links: SidebarLink[];
  config: LayoutConfig;
}

const SidebarContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'collapsed',
})<{ collapsed: boolean }>(({ theme, collapsed }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.background.paper,
  borderRight: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
  overflow: 'hidden',
}));

const SidebarHeader = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'collapsed',
})<{ collapsed: boolean }>(({ theme, collapsed }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: collapsed ? theme.spacing(2, 1) : theme.spacing(2),
  minHeight: 64,
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
  position: 'relative',
  justifyContent: collapsed ? 'center' : 'space-between',
}));

const LogoSection = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'collapsed',
})<{ collapsed: boolean }>(({ theme, collapsed }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: collapsed ? 0 : theme.spacing(1.5),
  flex: collapsed ? 'none' : 1,
  minWidth: 0,
  justifyContent: collapsed ? 'center' : 'flex-start',
}));

const CollapseButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
  width: 32,
  height: 32,
  position: 'absolute',
  right: 8,
  top: '50%',
  transform: 'translateY(-50%)',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    borderColor: theme.palette.primary.main,
  },
}));

const MenuContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: theme.spacing(1),
  '&::-webkit-scrollbar': {
    width: 6,
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: alpha(theme.palette.action.active, 0.2),
    borderRadius: 3,
    '&:hover': {
      backgroundColor: alpha(theme.palette.action.active, 0.3),
    },
  },
}));

const Sidebar: React.FC<SidebarProps> = ({ links, config }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { collapsed, mobileOpen, toggleCollapse, toggleMobile, getWidth } = useSidebar();
  
  const sidebarWidth = getWidth();
  const isCollapsed = collapsed && !isMobile;

  const sidebarContent = (
    <SidebarContainer collapsed={isCollapsed}>
      <SidebarHeader collapsed={isCollapsed}>
        <LogoSection collapsed={isCollapsed}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              height: 40,
              borderRadius: '12px',
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
              flexShrink: 0,
            }}
          >
            <Warehouse sx={{ fontSize: 24 }} />
          </Box>
          {!isCollapsed && (
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  fontSize: '1.1rem',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                Embassy App
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                Embassy App
              </Typography>
            </Box>
          )}
        </LogoSection>
        
        {!isMobile && config.allowSidebarToggle && (
          <CollapseButton onClick={toggleCollapse} size="small">
            {isCollapsed ? (
              <ChevronRight sx={{ fontSize: 18 }} />
            ) : (
              <ChevronLeft sx={{ fontSize: 18 }} />
            )}
          </CollapseButton>
        )}
        
        {isMobile && (
          <IconButton onClick={toggleMobile} size="small">
            <MenuIcon />
          </IconButton>
        )}
      </SidebarHeader>

      <MenuContainer>
        <SidebarMenu links={links} collapsed={isCollapsed} />
      </MenuContainer>

      {/* Footer */}
      {!isCollapsed && (
        <Box
          sx={{
            p: 2,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: 'text.disabled',
              fontSize: '0.7rem',
            }}
          >
            v2.1.0 © 2025 WMS
          </Typography>
        </Box>
      )}
    </SidebarContainer>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={toggleMobile}
        ModalProps={{
          keepMounted: true,
        }}
        PaperProps={{
          sx: {
            width: config.sidebarWidth.expanded,
            boxShadow: theme.shadows[8],
          },
        }}
      >
        {sidebarContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      PaperProps={{
        sx: {
          width: sidebarWidth,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.standard,
          }),
          overflowX: 'hidden',
          boxShadow: theme.shadows[2],
        },
      }}
    >
      {sidebarContent}
    </Drawer>
  );
};

export default Sidebar;